/**
 * Favorites Service
 *
 * Persists two parallel favorite lists in `localStorage` and exposes a small
 * subscribe/get/toggle API for both:
 *
 *   - favorite **connector IDs** (flat string list)
 *   - favorite **operations** (full `OperationDefinition` snapshots so the
 *     picker can render and add them without re-fetching)
 *
 * Both stores share the same in-memory cache + cross-tab `storage` sync via
 * the generic `createFavoritesStore` factory below.
 */

import type { OperationDefinition } from '@/types/operations';
import { telemetryService } from './telemetryService';

const CONNECTORS_STORAGE_KEY = 'otto.favoriteConnectors';
const OPERATIONS_STORAGE_KEY = 'otto.favoriteOperations';
const MAX_FAVORITES = 100;

const hasStorage = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
};

/**
 * Normalize a connector ID for stable storage / comparison.
 * Lower-cased, leading slashes stripped, whitespace trimmed.
 */
export const normalizeFavoriteId = (id: string): string =>
  id.trim().replace(/^\/+/, '').toLowerCase();

type FavoritesStoreOptions<T> = {
  storageKey: string;
  /** Identity key used for membership checks and adds. */
  getId: (item: T) => string;
  /** Hydration validator — invalid entries are dropped silently. */
  validate: (raw: unknown) => raw is T;
  /** Optional per-item normalization applied on read and write. */
  normalize?: (item: T) => T;
  /** Telemetry event names. */
  telemetry: { addedEvent: string; removedEvent: string; idProp: string };
  /** Optional extra telemetry properties on add (e.g. connectorId). */
  addedExtraProps?: (item: T) => Record<string, string>;
};

type FavoritesStore<T, AddArg = T> = {
  get: () => T[];
  has: (id: string) => boolean;
  add: (item: AddArg) => T[];
  remove: (id: string) => T[];
  toggle: (item: AddArg) => T[];
  subscribe: (listener: (favorites: T[]) => void) => () => void;
  /** Test-only: clear cache and storage. */
  reset: () => void;
  /** Internal: invoked on cross-tab storage events. */
  invalidateAndNotify: () => void;
};

const createFavoritesStore = <T>(opts: FavoritesStoreOptions<T>): FavoritesStore<T> => {
  const { storageKey, getId, validate, normalize, telemetry, addedExtraProps } = opts;
  const listeners = new Set<(favorites: T[]) => void>();
  let cache: T[] | null = null;

  const read = (): T[] => {
    if (!hasStorage()) return [];
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      const items = parsed.filter(validate);
      return normalize ? items.map(normalize) : items;
    } catch {
      return [];
    }
  };

  const write = (favorites: T[]): void => {
    if (!hasStorage()) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(favorites));
    } catch (error) {
      console.warn(`Failed to persist favorites (${storageKey}):`, error);
    }
  };

  const notify = (favorites: T[]): void => {
    for (const listener of listeners) {
      try {
        listener(favorites);
      } catch (error) {
        console.warn(`Favorites listener threw (${storageKey}):`, error);
      }
    }
  };

  const commit = (next: T[]): T[] => {
    cache = next;
    write(next);
    notify(next);
    return next;
  };

  const get = (): T[] => {
    if (cache === null) cache = read();
    return cache;
  };

  const has = (id: string): boolean => {
    if (!id) return false;
    return get().some((item) => getId(item) === id);
  };

  const add = (item: T): T[] => {
    if (!item) return get();
    const normalized = normalize ? normalize(item) : item;
    const id = getId(normalized);
    if (!id) return get();
    const current = get();
    if (current.some((i) => getId(i) === id)) return current;
    if (current.length >= MAX_FAVORITES) return current;
    telemetryService.trackEvent(telemetry.addedEvent, {
      [telemetry.idProp]: id,
      ...addedExtraProps?.(normalized),
    });
    return commit([...current, normalized]);
  };

  const remove = (id: string): T[] => {
    if (!id) return get();
    const current = get();
    if (!current.some((i) => getId(i) === id)) return current;
    telemetryService.trackEvent(telemetry.removedEvent, {
      [telemetry.idProp]: id,
    });
    return commit(current.filter((i) => getId(i) !== id));
  };

  const toggle = (item: T): T[] => {
    if (!item) return get();
    const id = getId(normalize ? normalize(item) : item);
    return has(id) ? remove(id) : add(item);
  };

  const subscribe = (listener: (favorites: T[]) => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const reset = (): void => {
    cache = null;
    if (!hasStorage()) return;
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  };

  const invalidateAndNotify = (): void => {
    cache = null;
    notify(get());
  };

  return { get, has, add, remove, toggle, subscribe, reset, invalidateAndNotify };
};

// ---------------------------------------------------------------------------
// Connector favorites (string IDs)
// ---------------------------------------------------------------------------

const connectorStore = createFavoritesStore<string>({
  storageKey: CONNECTORS_STORAGE_KEY,
  getId: (id) => id,
  validate: (v): v is string => typeof v === 'string' && v.length > 0,
  normalize: normalizeFavoriteId,
  telemetry: {
    addedEvent: 'connector.favorite.added',
    removedEvent: 'connector.favorite.removed',
    idProp: 'connectorId',
  },
});

export const getFavoriteConnectors = (): string[] => connectorStore.get();
export const isFavoriteConnector = (connectorId: string): boolean =>
  connectorStore.has(normalizeFavoriteId(connectorId));
export const addFavoriteConnector = (connectorId: string): string[] =>
  connectorStore.add(connectorId);
export const removeFavoriteConnector = (connectorId: string): string[] =>
  connectorStore.remove(normalizeFavoriteId(connectorId));
export const toggleFavoriteConnector = (connectorId: string): string[] =>
  connectorStore.toggle(connectorId);
export const subscribeFavorites = (
  listener: (favorites: string[]) => void,
): (() => void) => connectorStore.subscribe(listener);

// ---------------------------------------------------------------------------
// Operation favorites (full snapshots)
// ---------------------------------------------------------------------------

const isOperationDefinition = (v: unknown): v is OperationDefinition => {
  if (!v || typeof v !== 'object') return false;
  const op = v as Partial<OperationDefinition>;
  return (
    typeof op.id === 'string' &&
    op.id.length > 0 &&
    typeof op.name === 'string' &&
    typeof op.type === 'string' &&
    typeof op.category === 'string'
  );
};

const operationStore = createFavoritesStore<OperationDefinition>({
  storageKey: OPERATIONS_STORAGE_KEY,
  getId: (op) => op.id,
  validate: isOperationDefinition,
  telemetry: {
    addedEvent: 'operation.favorite.added',
    removedEvent: 'operation.favorite.removed',
    idProp: 'operationId',
  },
  addedExtraProps: (op) => ({ connectorId: op.connectorId ?? '' }),
});

export const getFavoriteOperations = (): OperationDefinition[] => operationStore.get();
export const isFavoriteOperation = (operationId: string): boolean =>
  operationStore.has(operationId);
export const addFavoriteOperation = (
  operation: OperationDefinition,
): OperationDefinition[] => operationStore.add(operation);
export const removeFavoriteOperation = (
  operationId: string,
): OperationDefinition[] => operationStore.remove(operationId);
export const toggleFavoriteOperation = (
  operation: OperationDefinition,
): OperationDefinition[] => operationStore.toggle(operation);
export const subscribeFavoriteOperations = (
  listener: (favorites: OperationDefinition[]) => void,
): (() => void) => operationStore.subscribe(listener);

// ---------------------------------------------------------------------------
// Cross-tab sync
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === CONNECTORS_STORAGE_KEY) {
      connectorStore.invalidateAndNotify();
    } else if (event.key === OPERATIONS_STORAGE_KEY) {
      operationStore.invalidateAndNotify();
    }
  });
}

/** Test-only: reset caches and clear storage for both stores. */
export const __resetFavoritesForTests = (): void => {
  connectorStore.reset();
  operationStore.reset();
};
